import React from 'react';
import { Card, Text, Progress, Group, Badge, Button, Stack } from '@mantine/core';
import { useSubscription } from '../contexts/SubscriptionContext';

const UsageIndicator = ({ feature, title }) => {
  const { getUsage, isNearLimit, isAtLimit, promptUpgrade, isProUser } = useSubscription();
  const usage = getUsage(feature);

  if (isProUser) {
    return (
      <Card shadow="sm" padding="md" radius="md" withBorder>
        <Group justify="space-between" align="center">
          <div>
            <Text size="sm" fw={500}>{title}</Text>
            <Text size="xs" c="dimmed">Unlimited</Text>
          </div>
          <Badge variant="gradient" gradient={{ from: 'yellow', to: 'orange' }}>
            ⭐ PRO
          </Badge>
        </Group>
      </Card>
    );
  }

  const getProgressColor = () => {
    if (usage.percentage >= 100) return 'red';
    if (usage.percentage >= 80) return 'orange';
    if (usage.percentage >= 60) return 'yellow';
    return 'teal';
  };

  const showWarning = isNearLimit(feature) || isAtLimit(feature);

  return (
    <Card 
      shadow="sm" 
      padding="md" 
      radius="md" 
      withBorder
      style={{
        borderColor: isAtLimit(feature) ? '#ff6b6b' : undefined,
        borderWidth: isAtLimit(feature) ? 2 : 1
      }}
    >
      <Stack gap="xs">
        <Group justify="space-between" align="flex-start">
          <div>
            <Text size="sm" fw={500}>{title}</Text>
            <Text size="xs" c="dimmed">
              {usage.current} of {usage.limit} used
            </Text>
          </div>
          
          {isAtLimit(feature) && (
            <Badge color="red" size="sm">
              Limit Reached
            </Badge>
          )}
          
          {!isAtLimit(feature) && isNearLimit(feature) && (
            <Badge color="orange" size="sm">
              {Math.round(usage.percentage)}% Used
            </Badge>
          )}
        </Group>

        <Progress
          value={usage.percentage}
          color={getProgressColor()}
          size="md"
          radius="sm"
          animated={isNearLimit(feature)}
        />

        {showWarning && (
          <>
            <Text size="xs" c={isAtLimit(feature) ? 'red' : 'orange'} fw={500}>
              {isAtLimit(feature) 
                ? `You've reached your ${title.toLowerCase()} limit. Upgrade to Pro for unlimited access!`
                : `You're almost at your limit. Upgrade to Pro for unlimited ${title.toLowerCase()}.`
              }
            </Text>
            
            <Button
              size="xs"
              variant="gradient"
              gradient={{ from: 'yellow', to: 'orange' }}
              fullWidth
              onClick={() => promptUpgrade({ 
                feature, 
                limit: usage.limit,
                message: `Upgrade to Pro for unlimited ${title.toLowerCase()}`
              })}
            >
              Upgrade to Pro ⭐
            </Button>
          </>
        )}
      </Stack>
    </Card>
  );
};

export default UsageIndicator;
